/*!
  * shared v10.0.8
  * (c) 2025 kazuya kawaguchi
  * Released under the MIT License.
  */
function warn(msg, err) {
    if (typeof console !== 'undefined') {
        console.warn("[intlify] " + msg);
        /* istanbul ignore if */
        if (err) {
            console.warn(err.stack);
        }
    }
}
var hasWarned = {};
function warnOnce(msg) {
    if (!hasWarned[msg]) {
        hasWarned[msg] = true;
        warn(msg);
    }
}
/**
 * Original Utilities
 * written by kazuya kawaguchi
 */
var inBrowser = typeof window !== 'undefined';
var mark;
var measure;
if ((process.env.NODE_ENV !== 'production')) {
    var perf_1 = inBrowser && window.performance;
    if (perf_1 &&
        perf_1.mark &&
        perf_1.measure &&
        perf_1.clearMarks &&
        // @ts-ignore browser compat
        perf_1.clearMeasures) {
        mark = function (tag) {
            perf_1.mark(tag);
        };
        measure = function (name, startTag, endTag) {
            perf_1.measure(name, startTag, endTag);
            perf_1.clearMarks(startTag);
            perf_1.clearMarks(endTag);
        };
    }
}
var RE_ARGS = /\{([0-9a-zA-Z]+)\}/g;
/* eslint-disable */
function format(message) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    if (args.length === 1 && isObject(args[0])) {
        args = args[0];
    }
    if (!args || !args.hasOwnProperty) {
        args = {};
    }
    return message.replace(RE_ARGS, function (match, identifier) {
        return args.hasOwnProperty(identifier) ? args[identifier] : '';
    });
}
var makeSymbol = function (name, shareable) {
    if (shareable === void 0) { shareable = false; }
    return !shareable ? Symbol(name) : Symbol.for(name);
};
var generateFormatCacheKey = function (locale, key, source) { return friendlyJSONstringify({ l: locale, k: key, s: source }); };
var friendlyJSONstringify = function (json) { return JSON.stringify(json)
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
    .replace(/\u0027/g, '\\u0027'); };
var isNumber = function (val) { return typeof val === 'number' && isFinite(val); };
var isDate = function (val) { return toTypeString(val) === '[object Date]'; };
var isRegExp = function (val) { return toTypeString(val) === '[object RegExp]'; };
var isEmptyObject = function (val) { return isPlainObject(val) && Object.keys(val).length === 0; };
var assign = Object.assign;
var _create = Object.create;
var create = function (obj) {
    if (obj === void 0) { obj = null; }
    return _create(obj);
};
var _globalThis;
var getGlobalThis = function () {
    // prettier-ignore
    return (_globalThis ||
        (_globalThis =
            typeof globalThis !== 'undefined'
                ? globalThis
                : typeof self !== 'undefined'
                    ? self
                    : typeof window !== 'undefined'
                        ? window
                        : typeof global !== 'undefined'
                            ? global
                            : create()));
};
function escapeHtml(rawText) {
    return rawText
        .replace(/&/g, '&amp;') // escape `&` first to avoid double escaping
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
        .replace(/\//g, '&#x2F;') // escape `/` to prevent closing tags or JavaScript URLs
        .replace(/=/g, '&#x3D;'); // escape `=` to prevent attribute injection
}
function escapeAttributeValue(value) {
    return value
        .replace(/&(?![a-zA-Z0-9#]{2,6};)/g, '&amp;') // escape unescaped `&`
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
function sanitizeTranslatedHtml(html) {
    // Escape dangerous characters in attribute values
    // Process attributes with double quotes
    html = html.replace(/(\w+)\s*=\s*"([^"]*)"/g, function (_, attrName, attrValue) { return "".concat(attrName, "=\"").concat(escapeAttributeValue(attrValue), "\""); });
    // Process attributes with single quotes
    html = html.replace(/(\w+)\s*=\s*'([^']*)'/g, function (_, attrName, attrValue) { return "".concat(attrName, "='").concat(escapeAttributeValue(attrValue), "'"); });
    // Detect and neutralize event handler attributes
    var eventHandlerPattern = /\s*on\w+\s*=\s*["']?[^"'>]+["']?/gi;
    if (eventHandlerPattern.test(html)) {
        if ((process.env.NODE_ENV !== 'production')) {
            warn('Potentially dangerous event handlers detected in translation. ' +
                'Consider removing onclick, onerror, etc. from your translation messages.');
        }
        // Neutralize event handler attributes by escaping 'on'
        html = html.replace(/(\s+)(on)(\w+\s*=)/gi, '$1&#111;n$3');
    }
    // Disable javascript: URLs in various contexts
    var javascriptUrlPattern = [
        // In href, src, action, formaction attributes
        /(\s+(?:href|src|action|formaction)\s*=\s*["']?)\s*javascript:/gi,
        // In style attributes within url()
        /(style\s*=\s*["'][^"']*url\s*\(\s*)javascript:/gi
    ];
    javascriptUrlPattern.forEach(function (pattern) {
        html = html.replace(pattern, '$1javascript&#58;');
    });
    return html;
}
var hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwn(obj, key) {
    return hasOwnProperty.call(obj, key);
}
/* eslint-enable */
/**
 * Useful Utilities By Evan you
 * Modified by kazuya kawaguchi
 * MIT License
 * https://github.com/vuejs/vue-next/blob/master/packages/shared/src/index.ts
 * https://github.com/vuejs/vue-next/blob/master/packages/shared/src/codeframe.ts
 */
var isArray = Array.isArray;
var isFunction = function (val) { return typeof val === 'function'; };
var isString = function (val) { return typeof val === 'string'; };
var isBoolean = function (val) { return typeof val === 'boolean'; };
var isSymbol = function (val) { return typeof val === 'symbol'; };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isObject = function (val) { return val !== null && typeof val === 'object'; };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var isPromise = function (val) {
    return isObject(val) && isFunction(val.then) && isFunction(val.catch);
};
var objectToString = Object.prototype.toString;
var toTypeString = function (value) { return objectToString.call(value); };
var isPlainObject = function (val) { return toTypeString(val) === '[object Object]'; };
// for converting list and named values to displayed strings.
var toDisplayString = function (val) {
    return val == null
        ? ''
        : isArray(val) || (isPlainObject(val) && val.toString === objectToString)
            ? JSON.stringify(val, null, 2)
            : String(val);
};
function join(items, separator) {
    if (separator === void 0) { separator = ''; }
    return items.reduce(function (str, item, index) { return (index === 0 ? str + item : str + separator + item); }, '');
}
var RANGE = 2;
function generateCodeFrame(source, start, end) {
    if (start === void 0) { start = 0; }
    if (end === void 0) { end = source.length; }
    var lines = source.split(/\r?\n/);
    var count = 0;
    var res = [];
    for (var i = 0; i < lines.length; i++) {
        count += lines[i].length + 1;
        if (count >= start) {
            for (var j = i - RANGE; j <= i + RANGE || end > count; j++) {
                if (j < 0 || j >= lines.length)
                    continue;
                var line = j + 1;
                res.push("".concat(line).concat(' '.repeat(3 - String(line).length), "|  ").concat(lines[j]));
                var lineLength = lines[j].length;
                if (j === i) {
                    // push underline
                    var pad = start - (count - lineLength) + 1;
                    var length = Math.max(1, end > count ? lineLength - pad : end - start);
                    res.push("   |  " + ' '.repeat(pad) + '^'.repeat(length));
                }
                else if (j > i) {
                    if (end > count) {
                        var length = Math.max(Math.min(end - count, lineLength), 1);
                        res.push("   |  " + '^'.repeat(length));
                    }
                    count += lineLength + 1;
                }
            }
            break;
        }
    }
    return res.join('\n');
}
/**
 * Event emitter, forked from the below:
 * - original repository url: https://github.com/developit/mitt
 * - code url: https://github.com/developit/mitt/blob/master/src/index.ts
 * - author: Jason Miller (https://github.com/developit)
 * - license: MIT
 */
/**
 * Create a event emitter
 *
 * @returns An event emitter
 */
function createEmitter() {
    var events = new Map();
    var emitter = {
        events: events,
        on: function (event, handler) {
            var handlers = events.get(event);
            var added = handlers && handlers.push(handler);
            if (!added) {
                events.set(event, [handler]);
            }
        },
        off: function (event, handler) {
            var handlers = events.get(event);
            if (handlers) {
                handlers.splice(handlers.indexOf(handler) >>> 0, 1);
            }
        },
        emit: function (event, payload) {
            (events.get(event) || [])
                .slice()
                .map(function (handler) { return handler(payload); });
            (events.get('*') || [])
                .slice()
                .map(function (handler) { return handler(event, payload); });
        }
    };
    return emitter;
}
var isNotObjectOrIsArray = function (val) { return !isObject(val) || isArray(val); };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepCopy(src, des) {
    // src and des should both be objects, and none of them can be a array
    if (isNotObjectOrIsArray(src) || isNotObjectOrIsArray(des)) {
        throw new Error('Invalid value');
    }
    var stack = [{ src: src, des: des }];
    var _loop_1 = function () {
        var _a = stack.pop(), src_1 = _a.src, des_1 = _a.des;
        // using `Object.keys` which skips prototype properties
        Object.keys(src_1).forEach(function (key) {
            if (key === '__proto__') {
                return;
            }
            // if src[key] is an object/array, set des[key]
            // to empty object/array to prevent setting by reference
            if (isObject(src_1[key]) && !isObject(des_1[key])) {
                des_1[key] = Array.isArray(src_1[key]) ? [] : create();
            }
            if (isNotObjectOrIsArray(des_1[key]) || isNotObjectOrIsArray(src_1[key])) {
                // replace with src[key] when:
                // src[key] or des[key] is not an object, or
                // src[key] or des[key] is an array
                des_1[key] = src_1[key];
            }
            else {
                // src[key] and des[key] are both objects, merge them
                stack.push({ src: src_1[key], des: des_1[key] });
            }
        });
    };
    while (stack.length) {
        _loop_1();
    }
}
export { assign, create, createEmitter, deepCopy, escapeHtml, format, friendlyJSONstringify, generateCodeFrame, generateFormatCacheKey, getGlobalThis, hasOwn, inBrowser, isArray, isBoolean, isDate, isEmptyObject, isFunction, isNumber, isObject, isPlainObject, isPromise, isRegExp, isString, isSymbol, join, makeSymbol, mark, measure, objectToString, sanitizeTranslatedHtml, toDisplayString, toTypeString, warn, warnOnce };
